import {createAction, props} from '@ngrx/store';
import {IStore} from '../../../shared/shared-models/store/store';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {IFeature, INavigate} from '../models';
import {NavigationPageEnum} from '../models/navigations.enums';

//#region GLOBAL
/**
 * Sets a dynamic property and its value on the user store state.
 * This is useful for updating a specific property without mutating the entire user object.
 *
 * @param property - The name of the property to update.
 * @param value - The new value for the specified property.
 */
export const setPropertyOnUserStoreState = createAction(
  '[USER STORES][Global] Set Property on User Store State',
  props<{
    property: string;
    value: unknown;
  }>(),
);

/**
 * Clears the user-store-related state from the global store.
 *
 * This action is typically dispatched when a user logs out
 * or when resetting the application state.
 *
 * @constant
 */
export const clearUserStoreState = createAction(
  '[USER STORES][Global] Clear User Store State',
);

/**
 * Updates the user's "last seen" timestamp in the store.
 *
 * This action is typically dispatched automatically by a heartbeat effect
 * to track ongoing user activity and session freshness.
 * It helps ensure accurate session monitoring and presence indication.
 *
 * @constant
 */
export const updateLastSeen = createAction(
  '[USER STORES][Last Seen] Update Last Seen',
);

//#endregion

//#region GET STORE INFORMATION

/**
 * Triggers the retrieval of store information for a given list of store IDs.
 *
 * This action is typically dispatched during login or when store data is required elsewhere in the app.
 * The `isLoggingIn` flag helps determine whether this call is part of the login sequence, which may
 * influence loading indicators or progress tracking.
 *
 * @param {string[]} storeIds - Array of store identifiers for which information is to be fetched.
 * @param {boolean} isLoggingIn - Indicates whether the fetch is part of a login process.
 */
export const getStoresInformation = createAction(
  '[USER STORES][Store] Get Stores Information',
  props<{ storeIds: string[], isLoggingIn: boolean }>(),
);

/**
 * Action dispatched when store information is successfully retrieved.
 *
 * @param {IStore[]} stores - An array of store objects retrieved from the backend.
 */
export const getStoresInformationSuccess = createAction(
  '[USER STORES][Store] Get Stores Information Success',
  props<{ stores: IStore[] }>(),
);

/**
 * Removes unusable stores from the active session only (shrink-only).
 * Does not write to the user's `user_access` document.
 */
export const evictStoresFromSession = createAction(
  '[USER STORES][Session] Evict Stores From Session',
  props<{
    storeIds: string[];
    reason: 'REVOKED' | 'DISABLED';
    storeNames?: string[];
  }>(),
);

/**
 * Queues a login-time warning for DISABLED stores. Shown once login progress
 * reaches 100% so the modal presents on the post-login shell (FE-95).
 */
export const queueDisabledStoresLoginNotice = createAction(
  '[USER STORES][Session] Queue Disabled Stores Login Notice',
  props<{
    storeNames: string[];
    storeIds: string[];
  }>(),
);

/**
 * Shows user feedback after one or more stores were removed from the session.
 * When `logOutAfterDismiss` is true, logout runs after the warning modal is dismissed
 */
export const showSessionStoreEvictionWarning = createAction(
  '[USER STORES][Session] Show Store Eviction Warning',
  props<{
    storeNames: string[];
    reason: 'REVOKED' | 'DISABLED' | 'MISSING';
    storeIds?: string[];
    logOutAfterDismiss?: boolean;
    /**
     * When true, DISABLED copy refers to stores excluded at login.
     * When false/omitted, DISABLED copy refers to mid-session removal.
     */
    isLoginNotice?: boolean;
  }>(),
);

//#endregion

//#region SET SELECTED

/**
 * Action to set the currently selected store directly using a store object.
 *
 * @param {IStore} selectedStore - The store object to set as selected.
 */
export const setSelectedStore = createAction(
  '[USER][Selected] Set Selected Store',
  props<{ selectedStore: IStore }>(),
);

/**
 * Confirms a selected-store change after page-access checks.
 * Feature effects that reload store-scoped data must listen to this action,
 * not `setSelectedStore`, so a blocked switch never loads unauthorized data.
 */
export const setSelectedStoreSuccess = createAction(
  '[USER][Selected] Set Selected Store Success',
  props<{ selectedStore: IStore }>(),
);

/**
 * Action to set the currently selected store using a store ID.
 *
 * @param {string} selectedStoreId - The ID of the store to set as selected.
 */
export const setSelectedStoreById = createAction(
  '[USER][Selected] Set Selected Store By Id',
  props<{ selectedStoreId: string }>(),
);

export const setSelectedStores = createAction(
  '[USER][Selected] Set Selected Stores',
  props<{ selected: IStore[] }>(),
);

export const setSelectedStoresById = createAction(
  '[USER][Selected] Set Selected Stores By Id',
  props<{ selected: string[] }>(),
);


/**
 * Action to set the currently selected feature.
 *
 * @param {IFeature} selectedFeature - The feature object to set as selected.
 */
export const setSelectedFeature = createAction(
  '[USER][Selected] Set Selected Feature',
  props<{ selectedFeature: IFeature }>(),
);

/**
 * Action to set the user's navigation state, including page and associated feature.
 *
 * @param {INavigate} navigation - The current navigation state including page and tab.
 * @param {NavigationPageEnum} feature - The main navigation page feature to associate with.
 */
export const setNavigation = createAction(
  '[USER][Navigation] Set User Navigation',
  props<{ navigation: INavigate, feature: NavigationPageEnum }>(),
);
/**
 * Action to set the user's navigation state, including page and associated feature.
 *
 * @param {INavigate} navigation - The current navigation state including page and tab.
 * @param {NavigationPageEnum} feature - The main navigation page feature to associate with.
 */
export const setNavigationSuccess = createAction(
  '[USER][Navigation] Set User Navigation Success',
  props<{ navigation: INavigate, feature: NavigationPageEnum, savedChanges?: boolean }>(),
);

/**
 * Syncs shell navigation/header selection from the active router URL without navigating.
 * Used for browser Back/Forward (and any router change that did not go through setNavigation).
 */
export const syncNavigationFromRouter = createAction(
  '[USER][Navigation] Sync Navigation From Router',
  props<{ navigation: INavigate, feature: NavigationPageEnum }>(),
);

export const showNavigationLoading = createAction(
  '[USER][Navigation] Show Navigation Loading',
  props<{ showLoadingIndicator: boolean }>(),
);

export const hideNavigationLoadingWithMinDuration = createAction(
  '[USER][Navigation] Hide Navigation Loading With Min Duration',
);

//#endregion


// this needs to be checked for removal
export const getUserStores = createAction(
  '[Core][User Stores] Get User Stores',
  props<{ pageStoreDocument: string }>(),
);
export const getUserStoresSuccess = createAction(
  '[Core][User Stores] Get User Stores Success',
);
export const getUserStoresFailure = createAction(
  '[Core][User Stores] Get User Stores Failure',
  props<{ error: IGlobalError }>(),
);

export const setAreChangesSaved = createAction(
  '[USER][Changes] Set Are Changes Saved',
  props<{ areChangesSaved: boolean }>(),
);


